import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const BACKEND_HOST = "https://itms.monconnect.com";

const PoliciesList = () => {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filterCert, setFilterCert] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const token = localStorage.getItem("accessToken");
        const res = await axios.get("/api/policies/view/", {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });

        setCerts(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Error loading policies:", err);
        setError("Failed to load policies. Please try again later.");
        setCerts([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const normalizeUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return `${BACKEND_HOST}${url}`;
  };

  const filteredCerts = useMemo(() => {
    let data = certs;

    if (filterCert) {
      data = data.filter(
        (cert) =>
          cert.certification_name.toLowerCase() === filterCert.toLowerCase()
      );
    }

    if (search.trim()) {
      const term = search.toLowerCase();
      data = data
        .map((cert) => {
          const matchingPolicies = cert.policies.filter((policy) =>
            policy.policy_name.toLowerCase().includes(term)
          );

          const certMatches = cert.certification_name
            .toLowerCase()
            .includes(term);

          if (certMatches || matchingPolicies.length > 0) {
            return {
              ...cert,
              policies: certMatches ? cert.policies : matchingPolicies,
            };
          }

          return null;
        })
        .filter(Boolean);
    }

    return data;
  }, [certs, filterCert, search]);

  const certificationOptions = ["", ...new Set(certs.map((c) => c.certification_name))];

  if (loading) {
    return <div className="page-shell"><div className="card loading">Loading policies...</div></div>;
  }

  if (error) {
    return (
      <div className="page-shell">
        <p className="error-text" style={{ padding: "14px 16px" }}>{error}</p>
      </div>
    );
  }

  return (
    <div className="page-shell">
      <section className="compact-hero">
        <h1>Company ISO Certifications and Policies</h1>
        <p>
          Browse certifications, search linked documents, and open certificates
          or policy files from one organized interface.
        </p>
      </section>

      <section className="filters-panel">
        <div className="filter-item" style={{ flex: "2 1 280px" }}>
          <label>Search</label>
          <input
            placeholder="Search certifications or policies"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="filter-item">
          <label>Certification</label>
          <select value={filterCert} onChange={(e) => setFilterCert(e.target.value)}>
            {certificationOptions.map((option, index) =>
              option ? (
                <option key={index} value={option}>
                  {option}
                </option>
              ) : (
                <option key={index} value="">
                  All certifications
                </option>
              )
            )}
          </select>
        </div>
      </section>

      {filteredCerts.length === 0 ? (
        <div className="card no-data">No matching policies found.</div>
      ) : (
        <section className="metric-grid">
          {filteredCerts.map((cert) => (
            <article key={cert.id} className="card" style={{ padding: "1.5rem" }}>
              <div className="header-row">
                <div>
                  <h2 style={{ margin: 0, fontSize: "1.35rem", color: "#10203d" }}>
                    {cert.certification_name}
                  </h2>
                  <p style={{ margin: "8px 0 0", color: "#65748f" }}>
                    {cert.policies.length} linked policy documents
                  </p>
                </div>

                {cert.certificate_file ? (
                  <a
                    href={normalizeUrl(cert.certificate_file)}
                    target="_blank"
                    rel="noreferrer"
                    className="primary-btn"
                  >
                    View Certificate
                  </a>
                ) : (
                  <div className="pill">No certificate uploaded</div>
                )}
              </div>

              <div style={{ marginTop: "18px", display: "grid", gap: "12px" }}>
                {cert.policies.length === 0 ? (
                  <div className="no-data">No policies uploaded yet.</div>
                ) : (
                  cert.policies.map((policy) => (
                    <div
                      key={policy.id}
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "12px",
                        padding: "14px 16px",
                        borderRadius: "18px",
                        background: "rgba(239,244,255,0.72)",
                        border: "1px solid rgba(16,42,113,0.08)",
                      }}
                    >
                      <div style={{ fontWeight: 700, color: "#10203d" }}>
                        {policy.policy_name}
                      </div>

                      {policy.file ? (
                        <a
                          href={normalizeUrl(policy.file)}
                          target="_blank"
                          rel="noreferrer"
                          className="secondary-btn"
                        >
                          View Document
                        </a>
                      ) : (
                        <span className="pill">No file</span>
                      )}
                    </div>
                  ))
                )}
              </div>
            </article>
          ))}
        </section>
      )}
    </div>
  );
};

export default PoliciesList;
